import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { CoreButton } from "../../core/ui/button/button";
import { CoreInput } from "../../core/ui/input/input";
import { CoreText, CoreTextType } from "../../core/ui/text/text";
import { ModalV2 } from "../../core/ui/modal/modal";
import { SessionControls } from "../authorization/session_controls";
import { Student } from "./student_model";
import { StudentStore } from "./student_store";
import "../task/task.css";
import "../exam/exam.css";

export const StudentScreen = observer(() => {
  const [store] = useState(() => new StudentStore());
  const [blocking, setBlocking] = useState<Student>();
  useEffect(() => {
    store.list();
  }, [store]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (
        store.editing !== undefined &&
        (store.password || store.username !== (store.editing?.username ?? ""))
      ) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [store]);
  return (
    <main className="task-page">
      <SessionControls />
      <header className="task-header">
        <CoreText as="h1" type={CoreTextType.big} text="Ученики" />
        <CoreButton
          filled
          disabled={store.busy}
          onClick={() => store.open(null)}
        >
          Создать ученика
        </CoreButton>
      </header>

      {store.notice && (
        <p role="status" className="success-banner">
          {store.notice}
        </p>
      )}
      {store.error && (
        <p role="alert" className="error-banner">
          {store.error}
        </p>
      )}
      {store.busy && <p role="status">Загрузка…</p>}
      <section className="panel">
        {!store.students.length && !store.busy && (
          <p>Учётных записей учеников пока нет.</p>
        )}
        {store.students.map((student) => (
          <article className="exam-row" key={student._id}>
            <div>
              <CoreText
                as="h2"
                type={CoreTextType.header}
                text={student.username}
              />
              <p className="muted">
                {student.isBlocked ? "Доступ заблокирован" : "Доступ открыт"}
              </p>
              <p className="student-performance">
                Успеваемость:{" "}
                <strong>
                  {student.performance?.percent == null
                    ? "—"
                    : `${student.performance.percent}%`}
                </strong>
              </p>
              <p className="muted">
                Завершено тестов: {student.performance?.completed || 0} ·
                Пройдено заданий: {student.performance?.tasks || 0}
              </p>
            </div>
            <div className="row-actions">
              <CoreButton to={`/student/${student._id}/results`}>
                Результаты
              </CoreButton>
              <CoreButton
                disabled={store.busy}
                onClick={() => store.open(student)}
              >
                Логин и пароль
              </CoreButton>
              <CoreButton
                disabled={store.busy}
                onClick={() => setBlocking(student)}
              >
                {student.isBlocked ? "Разблокировать" : "Заблокировать"}
              </CoreButton>
            </div>
          </article>
        ))}
        <div className="row-actions">
          {(store.page > 0 || store.students.length >= 10) && (
            <>
              <CoreButton
                disabled={store.busy || store.page === 0}
                onClick={() => store.list(store.page - 1)}
              >
                Назад
              </CoreButton>
              <span>Страница {store.page + 1}</span>
              <CoreButton
                disabled={store.busy || store.students.length < 10}
                onClick={() => store.list(store.page + 1)}
              >
                Далее
              </CoreButton>
            </>
          )}
          <CoreButton disabled={store.busy} onClick={() => store.list()}>
            Обновить
          </CoreButton>
        </div>
      </section>
      {store.editing !== undefined && (
        <ModalV2 isOpen titleId="student-title" onClose={store.close}>
          <CoreText
            as="h2"
            id="student-title"
            type={CoreTextType.header}
            text={store.editing ? "Логин и пароль ученика" : "Создать ученика"}
          />
          <form
            onSubmit={(event) => {
              event.preventDefault();
              store.save();
            }}
          >
            <CoreInput
              native
              label="Логин"
              value={store.username}
              onChange={store.setUsername}
              disabled={store.busy}
              maxLength={64}
              autoComplete="off"
            />
            <CoreInput
              native
              label={store.editing ? "Новый пароль" : "Пароль"}
              inputType="password"
              autoComplete="new-password"
              value={store.password}
              onChange={store.setPassword}
              disabled={store.busy}
              maxLength={1024}
            />
            <p>
              {store.editing
                ? "Оставьте пароль пустым, чтобы сохранить прежний. Новый пароль завершит все сессии ученика."
                : "Не менее 12 символов. Сохраните пароль перед отправкой формы: повторно прочитать его нельзя."}
            </p>
            {store.error && <p role="alert">{store.error}</p>}
            <div className="row-actions">
              <CoreButton disabled={store.busy} onClick={store.close}>
                Отмена
              </CoreButton>
              <CoreButton filled type="submit" disabled={store.busy}>
                {store.busy ? "Сохранение…" : "Сохранить"}
              </CoreButton>
            </div>
          </form>
        </ModalV2>
      )}
      {blocking && (
        <ModalV2
          isOpen
          titleId="block-student-title"
          onClose={() => {
            if (!store.busy) setBlocking(undefined);
          }}
        >
          <CoreText
            as="h2"
            id="block-student-title"
            type={CoreTextType.header}
            text={`${blocking.isBlocked ? "Восстановить" : "Заблокировать"} доступ: ${blocking.username}?`}
          />
          <p>
            {blocking.isBlocked
              ? "Ученик снова сможет войти с действующим паролем."
              : "Ученик не сможет войти. Его текущие сессии завершатся; попытки сохранятся."}
          </p>
          {store.error && <p role="alert">{store.error}</p>}
          <div className="row-actions">
            <CoreButton
              disabled={store.busy}
              onClick={() => setBlocking(undefined)}
            >
              Отмена
            </CoreButton>
            <CoreButton
              disabled={store.busy}
              onClick={async () => {
                if (await store.setBlocked(blocking)) setBlocking(undefined);
              }}
            >
              Подтвердить
            </CoreButton>
          </div>
        </ModalV2>
      )}
    </main>
  );
});
